import React from 'react';
import { Typography } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { HttpResponse } from '../../services/base.service';
import { AppBox } from '../../components/AppBox';
import { AppFieldProps } from '../../types/components.type';
import { ExerciseType } from '../../types/exercise.type';
import { ExercisesServices } from '../../services/exercises.service';
import { ExercisePicker } from '../common/ExercisePicker';
import { GroupRoutineType } from '../../types/grouproutines.type';

export type GroupRoutineFieldProps = {
    phase: GroupRoutineType['phase'];
} & AppFieldProps

export const GroupRoutineExercisesField: React.FC<GroupRoutineFieldProps> = (props: GroupRoutineFieldProps) => {
    const { control, setValue, phase } = props;

    const controlName = 'group_routine_exercises';
    const [exercises, setExercises] = React.useState<ExerciseType[]>([]);

    React.useEffect(() => {
        const fn = phase == 'warmup' ? ExercisesServices.getWarmUpExercises : ExercisesServices.getAll;
        fn().then((resp: HttpResponse<ExerciseType[]>) => {
            setExercises(resp.data);
        })
    }, []);

    const exercisesCount = useWatch({
        control,
        name: 'exercises_count',
        defaultValue: control._formValues['exercises_count'] || 0,
    });

    /**
     * Whatch to force a render after the follow useEffect set the random exercises for the warmup
     */
    useWatch({
        control,
        name: controlName,
        defaultValue: control._formValues[controlName] || [],
    });

    React.useEffect(() => {
        if (exercises.length > 0) {
            control._formValues[controlName] = control._formValues[controlName] || [];

            // Initialize random exercises if they are not set
            let updatedValues = [...(control._formValues[controlName] || [])];
            // Slice array to have the exercisesCount number as maximum size
            updatedValues = updatedValues.slice(0, exercisesCount)
            for (let i = 0; i < exercisesCount; i++) {
                if (!updatedValues[i]) {
                    const randomExercise = getRandomExercise();
                    updatedValues[i] = {
                        exercise_id: randomExercise.id,
                        exercise: randomExercise,
                    };
                }
            }
            // Set the updated values in the form
            setValue?.(controlName, updatedValues);
        }
    }, [exercises, exercisesCount]);

    // Generate a random exercise
    const getRandomExercise = (): ExerciseType => {
        const randomIndex = Math.floor(Math.random() * exercises.length);
        return exercises[randomIndex];
    };

    const handleExerciseChange = (index: number, exerciseId: number, field: any) => {
        const updatedValues = field.value ? [...field.value] : [];
        updatedValues[index] = {
            exercise_id: exerciseId,
            exercise: exercises.find((e) => e.id === exerciseId)
        };
        // Notify React Hook Form of the change
        field.onChange(updatedValues);
    };

    const validate = (value: any = []) => {
        return value.length === parseInt(exercisesCount) || `All the exercises must be defined!`;
    }

    return (
        <Controller
            name={controlName}
            control={control}
            rules={{
                validate: validate
            }}
            render={({ field, fieldState }) => (
                <>
                    {fieldState.error && (
                        <AppBox>
                            <Typography color="error">
                                {fieldState.error.message}
                            </Typography>
                        </AppBox>
                    )}
                    <>
                        {Array.from({ length: exercisesCount }).map((_, index: number) => (
                            <ExercisePicker
                                key={index}
                                label={`Exercise ${index}`}
                                onChange={(exerciseId: number) => {
                                    handleExerciseChange(index, exerciseId, field);
                                }}
                                options={exercises}
                                exercise={field.value && field.value[index] ? field.value[index].exercise : null}
                            />

                        ))}
                    </>
                </>
            )}
        />

    );
};



